import type { ScheduleSession } from '../types/schedule.types';
import type { EventDetails } from '../types/eventDetails.types';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

// Helper function to extract the actual date from session data
export const extractSessionDate = (session: any): string => {
  // For V1 sessions, try startsAt first (ISO datetime with timezone)
  if (session.startsAt) {
    try {
      return new Date(session.startsAt).toISOString().split('T')[0];
    } catch {
      // If parsing fails, fall through to other methods
    }
  }
  
  // For V2 sessions, try startTime (raw datetime from API)
  if (session.startTime) {
    try {
      return new Date(session.startTime).toISOString().split('T')[0];
    } catch {
      // If parsing fails, fall through to fallback
    }
  }
  
  // Fallback to today's date
  return new Date().toISOString().split('T')[0];
};

// Helper function to get timezone abbreviation
const getTimeZoneAbbreviation = (timeZone: string, date: Date): string => {
  try {
    // Format the date in the given timezone to get the abbreviation
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short'
    }).format(date);
    
    // Extract the timezone abbreviation from the formatted string
    const match = formatted.match(/[A-Z]{2,4}$/);
    return match ? match[0] : '';
  } catch {
    // Fallback for invalid timezones
    return '';
  }
};

// Generate calendar URL for adding session to calendar
export const generateCalendarUrl = (
  session: ScheduleSession, 
  calendarType: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo', 
  eventDate?: string,
  eventTimeZone?: string
) => {
  // IMPORTANT: Prefer the eventDate parameter if provided, as session times often have placeholder dates
  // The eventDate should be the actual day's date from the agenda
  const dateToUse = eventDate || extractSessionDate(session) || new Date().toISOString().split('T')[0];
  
  // Parse the session times (they are in the event's timezone)
  // Convert time format "9:00 AM" to "09:00:00"
  const parseTimeToISO = (timeStr: string) => {
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    const paddedHour = hour.toString().padStart(2, '0');
    const paddedMinutes = minutes.padStart(2, '0');
    
    return `${dateToUse}T${paddedHour}:${paddedMinutes}:00`;
  };
  
  const startDateTimeStr = parseTimeToISO(session.time);
  const endDateTimeStr = parseTimeToISO(session.endTime);
  
  // If we have a timezone, convert to UTC for calendar applications
  let startDate: Date;
  let endDate: Date;
  
  if (eventTimeZone) {
    // Create dates in the event timezone and convert to UTC
    startDate = fromZonedTime(startDateTimeStr, eventTimeZone);
    endDate = fromZonedTime(endDateTimeStr, eventTimeZone);
  } else {
    // Fallback to local time (old behavior)
    startDate = new Date(`${dateToUse} ${session.time}`);
    endDate = new Date(`${dateToUse} ${session.endTime}`);
  }

  const formatGoogleDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
  };

  const title = encodeURIComponent(session.title);
  const location = encodeURIComponent(session.room || 'Event Location');
  
  // Build the event description with time and location info
  let descriptionLines = [];
  
  // Add session time with timezone abbreviation
  const timeZoneAbbr = eventTimeZone ? getTimeZoneAbbreviation(eventTimeZone, startDate) : '';
  descriptionLines.push(`Time: ${session.time} - ${session.endTime}${timeZoneAbbr ? ' ' + timeZoneAbbr : ''}`);
  
  // Add location
  if (session.room) {
    descriptionLines.push(`Location: ${session.room}`);
  }
  
  // Add a separator
  descriptionLines.push('');
  
  // Add the main description
  if (session.description) {
    descriptionLines.push(session.description);
  }
  
  // Add speakers
  if (session.speakers && session.speakers.length > 0) {
    descriptionLines.push('');
    descriptionLines.push(`Speakers: ${session.speakers.map(s => s.name).join(', ')}`);
  }
  
  const details = encodeURIComponent(descriptionLines.join('\n'));

  switch (calendarType) {
    case 'google':
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;

    case 'outlook':
    case 'office365':
      return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;

    case 'outlookcom':
      return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;

    case 'yahoo':
      // Yahoo Calendar expects dates in a specific format
      const yahooStartDate = startDate.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
      const yahooEndDate = endDate.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
      return `https://calendar.yahoo.com/?v=60&title=${title}&desc=${details}&in_loc=${location}&st=${yahooStartDate}&et=${yahooEndDate}`;

    case 'apple':
      // For ICS files, we need to escape special characters and handle line breaks
      const escapeICSText = (text: string): string => {
        return text
          .replace(/\\/g, '\\\\')
          .replace(/;/g, '\\;')
          .replace(/,/g, '\\,')
          .replace(/\n/g, '\\n');
      };
      
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Event Calendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `DTSTART:${formatICSDate(startDate)}Z`,
        `DTEND:${formatICSDate(endDate)}Z`,
        `SUMMARY:${escapeICSText(decodeURIComponent(title))}`,
        `DESCRIPTION:${escapeICSText(decodeURIComponent(details))}`,
        `LOCATION:${escapeICSText(decodeURIComponent(location))}`,
        `UID:${session.id}@event.calendar`,
        `DTSTAMP:${formatICSDate(new Date())}Z`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      return URL.createObjectURL(blob);

    default:
      return '#';
  }
};

// Generate calendar URL for adding entire event to calendar
export const generateEventCalendarUrl = (
  event: EventDetails,
  calendarType: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo'
) => {
  // Parse the event dates
  let startDate: Date;
  let endDate: Date;
  
  if (event.timeZone && event.startsAt && event.endsAt) {
    // If we have timezone info, use it
    startDate = new Date(event.startsAt);
    endDate = new Date(event.endsAt);
  } else if (event.startsAt && event.endsAt) {
    // Fallback to basic date parsing
    startDate = new Date(event.startsAt);
    endDate = new Date(event.endsAt);
  } else {
    // No dates available
    return '#';
  }

  const formatGoogleDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
  };

  const escapeICSText = (text: string) => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '');
  };

  const title = encodeURIComponent(event.name || 'Event');
  const location = event.location
    ? encodeURIComponent(
        event.location.fullAddress ||
        (event.location.address1 && event.location.city && event.location.state
          ? `${event.location.address1}, ${event.location.city}, ${event.location.state} ${event.location.zip || ''}`.trim()
          : 'Event Location')
      )
    : encodeURIComponent('Event Location');
  
  const details = encodeURIComponent(
    event.description ? 
      event.description.replace(/<[^>]*>/g, '').substring(0, 500) : 
      ''
  );

  // Check if the event spans multiple days (for all-day event handling)
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();
  const isAllDay = !event.showStartTime && !event.showEndTime;

  switch (calendarType) {
    case 'google':
      // Google Calendar URL format
      // For all-day events, use dates parameter instead of dates with times
      if (isAllDay) {
        // For all-day events, Google expects YYYYMMDD format without time
        const startDateStr = startDate.toISOString().split('T')[0].replace(/-/g, '');
        // For all-day events, end date should be the day after the last day
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        const endDateStr = adjustedEndDate.toISOString().split('T')[0].replace(/-/g, '');
        return `https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&dates=${startDateStr}/${endDateStr}&details=${details}&location=${location}&sf=true&output=xml`;
      } else {
        return `https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;
      }

    case 'outlook':
    case 'apple':
      // Generate ICS file content for Outlook desktop and Apple Calendar
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Event//Event Calendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        isAllDay
          ? `DTSTART;VALUE=DATE:${startDate.toISOString().split('T')[0].replace(/-/g, '')}`
          : `DTSTART:${formatICSDate(startDate)}Z`,
        isAllDay
          ? `DTEND;VALUE=DATE:${endDate.toISOString().split('T')[0].replace(/-/g, '')}`
          : `DTEND:${formatICSDate(endDate)}Z`,
        `SUMMARY:${escapeICSText(decodeURIComponent(title))}`,
        `DESCRIPTION:${escapeICSText(decodeURIComponent(details))}`,
        `LOCATION:${escapeICSText(decodeURIComponent(location))}`,
        `UID:event-${event.slug}@event.calendar`,
        `DTSTAMP:${formatICSDate(new Date())}Z`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');
      
      // Create a data URL for the ICS file
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      return URL.createObjectURL(blob);

    case 'outlookcom':
    case 'office365':
      // Outlook.com and Office 365 web URL format
      const outlookStartDate = isAllDay
        ? startDate.toISOString().split('T')[0]
        : startDate.toISOString();
      const outlookEndDate = isAllDay
        ? endDate.toISOString().split('T')[0]
        : endDate.toISOString();
      
      const outlookUrl = calendarType === 'office365'
        ? 'https://outlook.office.com/calendar/0/deeplink/compose'
        : 'https://outlook.live.com/calendar/0/deeplink/compose';
      
      return `${outlookUrl}?subject=${title}&body=${details}&location=${location}&startdt=${outlookStartDate}&enddt=${outlookEndDate}&allday=${isAllDay ? 'true' : 'false'}`;

    case 'yahoo':
      // Yahoo Calendar URL format
      if (isAllDay) {
        // For all-day events in Yahoo
        const yahooStartDate = startDate.toISOString().split('T')[0].replace(/-/g, '');
        const yahooEndDate = endDate.toISOString().split('T')[0].replace(/-/g, '');
        return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${title}&st=${yahooStartDate}&et=${yahooEndDate}&desc=${details}&in_loc=${location}&uid=`;
      } else {
        const yahooStartDate = formatGoogleDate(startDate);
        const yahooEndDate = formatGoogleDate(endDate);
        return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${title}&st=${yahooStartDate}&et=${yahooEndDate}&desc=${details}&in_loc=${location}&uid=`;
      }

    default:
      return '#';
  }
};